import { useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { RoutePath } from '@/core/config/routeConfig';
import type { MessageTemplateResponse } from '@/core/generated/api';
import { isBadRequestError } from '@/core/api/badRequestError';
import { messageTemplateService } from '@/entities/MessageTemplate/api/messageTemplateService';
import { messageTemplateFormValuesToRequest } from '@/entities/MessageTemplate/mappers';
import type { MessageTemplateFormValues } from '@/entities/MessageTemplate/model/schema';
import { getErrorMessage } from '@/shared/lib/errors';
import { useCanAccessPermission } from '@/shared/model/adminAccess';

/** Сохраняет текстовку приложения */
export const useMessageTemplateSubmit = (
    form: UseFormReturn<MessageTemplateFormValues>,
    current: MessageTemplateResponse | null,
) => {
    const navigate = useNavigate();
    const canEdit = useCanAccessPermission('admin.message-templates.manage');
    const ownerRef = useRef<{ active: boolean; pending: boolean } | null>(null);

    useEffect(() => {
        const owner = {
            active: true,
            pending: false
        };

        ownerRef.current = owner;

        return () => { owner.active = false; };
    }, [current?.id]);

    /** Отправляет изменения сообщения */
    const onSubmit = async (values: MessageTemplateFormValues) => {
        const owner = ownerRef.current;
        const canSave = canEdit && current?.id && form.formState.isDirty && owner?.active && !owner.pending;

        if (!canSave || !current?.id || !owner) return;
        owner.pending = true;

        try {
            await messageTemplateService.update(current.id, messageTemplateFormValuesToRequest(values, current));

            if (!owner.active) return;
            toast.success('Текстовка сохранена');

            navigate(current.group?.id
                ? RoutePath.messageTemplateGroup.replace(':group', current.group.id)
                : RoutePath.messageTemplates);
        } catch (error: unknown) {
            if (owner.active && !isBadRequestError(error)) {
                form.setError('root.server', {
                    type: 'server',
                    message: getErrorMessage(error, 'Не удалось сохранить текстовку'),
                });
            }
        } finally {
            owner.pending = false;
        }
    };

    return {
        onSubmit,
        canEdit
    };
};
